import { Checkbox as RadixCheckbox } from "radix-ui";
import { useId } from "react";
import { Icon } from "./Icon.tsx";
import { cx } from "./tokens.ts";

/** A control's name: a visible `label`, or an `accessibilityLabel` when the row around it already says what it is. */
export type ControlName = { label: string; accessibilityLabel?: string } | { label?: undefined; accessibilityLabel: string };

export type CheckboxProps = ControlName & {
  value: boolean;
  onChange: (value: boolean) => void;
  /** "Some but not all" (a parent of partly checked items): a dash, `aria-checked="mixed"`; pressing it reports `true`. */
  indeterminate?: boolean;
  /** Not pressable; dimmed (read-only database cells, locked settings). */
  disabled?: boolean;
  /** Just the box, no minimum row height: inline with a line of text (editor to-dos). */
  compact?: boolean;
  testID: string;
};

export function Checkbox({ value, onChange, label, accessibilityLabel, indeterminate, disabled, compact, testID }: CheckboxProps) {
  const id = useId();
  return (
    <div className={cx("b-control-row", "b-control-row-start", compact === true && "b-control-row-compact", disabled === true && "b-control-disabled")}>
      <RadixCheckbox.Root
        id={id}
        className="b-checkbox"
        checked={indeterminate === true ? "indeterminate" : value}
        onCheckedChange={(v) => onChange(v === true || v === "indeterminate")}
        disabled={disabled}
        aria-label={label === undefined ? accessibilityLabel : undefined}
        data-testid={testID}
      >
        <RadixCheckbox.Indicator className="b-checkbox-indicator">
          {indeterminate === true ? <span className="b-checkbox-dash" /> : <Icon name="check" size="icon.sm" />}
        </RadixCheckbox.Indicator>
      </RadixCheckbox.Root>
      {label === undefined ? null : (
        <label className="b-control-label" htmlFor={id}>
          {label}
        </label>
      )}
    </div>
  );
}
